import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp, Trophy } from "lucide-react";
import api from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import ScorecardBoard from "./ScorecardBoard";
import { useScorecard } from "./useScorecard";

// Thin always-on strip (Home + Dashboard). Dark theme → white bar, light theme → navy bar.
export default function ScorecardBanner() {
  const { theme } = useTheme() || {};
  const light = theme === "light";
  const [open, setOpen] = useState(() => localStorage.getItem("hv.scorecard.open") === "1");
  const [move, setMove] = useState({ has_new: false, changed_member_ids: [] });
  const sc = useScorecard();

  const [holdUntil, setHoldUntil] = useState(0);
  useEffect(() => {
    if (Date.now() < holdUntil) return; // keep the "what changed" highlight visible for its window
    api.get("/scorecard/movement").then(({ data }) => setMove(data)).catch(() => {});
  }, [sc.projects]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seeing the open board counts as "seen" — whenever it's open and there's news.
  useEffect(() => {
    if (open && move.has_new) {
      setHoldUntil(Date.now() + 9000);
      api.post("/scorecard/seen").catch(() => {});
      const t = setTimeout(() => setMove((m) => ({ ...m, has_new: false })), 8000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, move.has_new]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem("hv.scorecard.open", next ? "1" : "0");
  };

  if (!sc.loading && !sc.projects.length && !sc.can_edit) return null;
  // Inline colours: the light theme remaps .text-white, which would hide the label.
  const barStyle = light ? { background: "#0B1F3A", color: "#ffffff" } : { background: "#ffffff", color: "#0B1F3A" };
  return (
    <div className="mb-3" data-testid="scorecard-banner">
      <button
        onClick={toggle}
        className={`w-full h-8 px-3 rounded-md flex items-center gap-2 text-xs font-semibold ${move.has_new ? "animate-pulse ring-2 ring-amber-400" : ""}`}
        style={barStyle}
        data-testid="scorecard-banner-toggle"
      >
        <Trophy className="w-3.5 h-3.5" />
        <span>Scorecard</span>
        <span className="font-normal opacity-70">· {sc.projects.length} active project{sc.projects.length === 1 ? "" : "s"}</span>
        {move.has_new && <span className="ml-1 px-1.5 rounded text-[10px]" style={{ background: "#fbbf24", color: "#0B1F3A" }} data-testid="scorecard-banner-new-dot">New on your line</span>}
        <span className="ml-auto">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
      </button>
      {open && (
        <div className="mt-2 rounded-lg border border-white/10 p-3 space-y-2" style={{ background: light ? "#ffffff" : "#0c0f15" }} data-testid="scorecard-panel">
          <ScorecardBoard projects={sc.projects} canEdit={sc.can_edit} highlightIds={move.changed_member_ids} onChanged={sc.reload} />
          <div className="flex justify-between items-center pt-1">
            <Link to="/team/scorecard" className="text-[11px] text-cyan-300 hover:underline" data-testid="scorecard-open-full">Open full scorecard →</Link>
            <button onClick={toggle} className="text-white/50 hover:text-white" data-testid="scorecard-banner-fold"><ChevronUp className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
